import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import type { Database } from '@/lib/database.types';

type Category = Database['public']['Tables']['categories']['Row'];
type CategoryInsert = Database['public']['Tables']['categories']['Insert'];
type CategoryGroup = Database['public']['Tables']['category_groups']['Row'];
type CategoryGroupInsert = Database['public']['Tables']['category_groups']['Insert'];

export interface CategoryWithGroup extends Category {
  group?: CategoryGroup;
}

export interface GroupWithCategories extends CategoryGroup {
  categories: Category[];
}

// Extended icon mapping for display (includes emojis)
export const CATEGORY_ICONS: Record<string, string> = {
  // Income icons
  briefcase: '💼',
  laptop: '💻',
  'trending-up': '📈',
  gift: '🎁',
  'plus-circle': '➕',
  building: '🏢',
  wallet: '👛',
  'piggy-bank': '🐷',
  banknote: '💵',
  coins: '🪙',
  house: '🏠',

  // Expense icons
  utensils: '🍽️',
  car: '🚗',
  home: '🏠',
  zap: '⚡',
  'shopping-bag': '🛍️',
  heart: '❤️',
  'gamepad-2': '🎮',
  coffee: '☕',
  'graduation-cap': '🎓',
  repeat: '🔄',
  plane: '✈️',
  'more-horizontal': '📦',
  droplet: '💧',
  wifi: '📶',
  phone: '📱',
  shield: '🛡️',
  dumbbell: '🏋️',
  tv: '📺',
  music: '🎵',
  apple: '🍎',
  fuel: '⛽',
  toll: '🛣️',
  wrench: '🔧',
  parking: '🅿️',
  bus: '🚌',
  pill: '💊',
  stethoscope: '🩺',
  pizza: '🍕',
  film: '🎬',
  shirt: '👕',
  luggage: '🧳',
  baby: '👶',
  book: '📚',
  toy: '🧸',
  globe: '🌍',
  family: '👨‍👩‍👧‍👦',
  'hand-heart': '🤲',
  charity: '🎗️',
  tag: '🏷️',
  receipt: '🧾',
  default: '📦',
};

// Default category groups structure based on the French template
export const DEFAULT_GROUPS: Omit<CategoryGroupInsert, 'user_id'>[] = [
  // Income group
  { name: 'Revenus', type: 'income', display_order: 0, is_default: true },

  // Expense groups
  { name: 'Logement', type: 'expense', display_order: 0, is_default: true },
  { name: 'Factures', type: 'expense', display_order: 1, is_default: true },
  { name: 'Abonnements', type: 'expense', display_order: 2, is_default: true },
  { name: 'Courses & Vie quotidienne', type: 'expense', display_order: 3, is_default: true },
  { name: 'Transport & Déplacements', type: 'expense', display_order: 4, is_default: true },
  { name: 'Santé', type: 'expense', display_order: 5, is_default: true },
  { name: 'Vie sociale & Plaisir', type: 'expense', display_order: 6, is_default: true },
  { name: 'Enfants', type: 'expense', display_order: 7, is_default: true },
  { name: 'Famille & Solidarité', type: 'expense', display_order: 8, is_default: true },
  { name: 'Cadeaux & Dons', type: 'expense', display_order: 9, is_default: true },
  { name: 'Autres', type: 'expense', display_order: 10, is_default: true },
];

// Default categories mapped to groups (group_name is temporary, will be replaced with group_id)
export const DEFAULT_CATEGORIES_WITH_GROUPS: (Omit<CategoryInsert, 'user_id' | 'group_id'> & {
  group_name: string;
})[] = [
  // Income - Revenus
  {
    name: 'Salaire',
    icon: 'briefcase',
    color: '#16c47f',
    type: 'income',
    is_default: true,
    display_order: 0,
    group_name: 'Revenus',
  },
  {
    name: 'Freelance / Business',
    icon: 'laptop',
    color: '#3b82f6',
    type: 'income',
    is_default: true,
    display_order: 1,
    group_name: 'Revenus',
  },
  {
    name: 'Aides & Allocations',
    icon: 'hand-heart',
    color: '#8b5cf6',
    type: 'income',
    is_default: true,
    display_order: 2,
    group_name: 'Revenus',
  },
  {
    name: 'Revenus locatifs',
    icon: 'house',
    color: '#f59e0b',
    type: 'income',
    is_default: true,
    display_order: 3,
    group_name: 'Revenus',
  },
  {
    name: 'Revenus financiers',
    icon: 'trending-up',
    color: '#10b981',
    type: 'income',
    is_default: true,
    display_order: 4,
    group_name: 'Revenus',
  },
  {
    name: 'Autres revenus',
    icon: 'plus-circle',
    color: '#6b7280',
    type: 'income',
    is_default: true,
    display_order: 5,
    group_name: 'Revenus',
  },

  // Expense - Logement
  {
    name: 'Loyer',
    icon: 'home',
    color: '#8b5cf6',
    type: 'expense',
    is_default: true,
    display_order: 0,
    group_name: 'Logement',
  },
  {
    name: 'Crédit immobilier',
    icon: 'building',
    color: '#6366f1',
    type: 'expense',
    is_default: true,
    display_order: 1,
    group_name: 'Logement',
  },
  {
    name: 'Entretien ou réparation',
    icon: 'wrench',
    color: '#f59e0b',
    type: 'expense',
    is_default: true,
    display_order: 2,
    group_name: 'Logement',
  },
  {
    name: 'Assurance habitation',
    icon: 'shield',
    color: '#3b82f6',
    type: 'expense',
    is_default: true,
    display_order: 3,
    group_name: 'Logement',
  },

  // Expense - Factures
  {
    name: 'Électricité & gaz',
    icon: 'zap',
    color: '#f59e0b',
    type: 'expense',
    is_default: true,
    display_order: 0,
    group_name: 'Factures',
  },
  {
    name: 'Eau',
    icon: 'droplet',
    color: '#3b82f6',
    type: 'expense',
    is_default: true,
    display_order: 1,
    group_name: 'Factures',
  },
  {
    name: 'Internet',
    icon: 'wifi',
    color: '#6366f1',
    type: 'expense',
    is_default: true,
    display_order: 2,
    group_name: 'Factures',
  },
  {
    name: 'Téléphone mobile',
    icon: 'phone',
    color: '#10b981',
    type: 'expense',
    is_default: true,
    display_order: 3,
    group_name: 'Factures',
  },
  {
    name: 'Assurances',
    icon: 'shield',
    color: '#ef4444',
    type: 'expense',
    is_default: true,
    display_order: 4,
    group_name: 'Factures',
  },

  // Expense - Abonnements
  {
    name: 'Salle de sport',
    icon: 'dumbbell',
    color: '#10b981',
    type: 'expense',
    is_default: true,
    display_order: 0,
    group_name: 'Abonnements',
  },
  {
    name: 'Netflix',
    icon: 'tv',
    color: '#ef4444',
    type: 'expense',
    is_default: true,
    display_order: 1,
    group_name: 'Abonnements',
  },
  {
    name: 'Spotify',
    icon: 'music',
    color: '#16c47f',
    type: 'expense',
    is_default: true,
    display_order: 2,
    group_name: 'Abonnements',
  },
  {
    name: 'Apple iCloud',
    icon: 'cloud',
    color: '#6b7280',
    type: 'expense',
    is_default: true,
    display_order: 3,
    group_name: 'Abonnements',
  },
  {
    name: 'Autres abonnements',
    icon: 'repeat',
    color: '#8b5cf6',
    type: 'expense',
    is_default: true,
    display_order: 4,
    group_name: 'Abonnements',
  },

  // Expense - Courses & Vie quotidienne
  {
    name: 'Courses alimentaires',
    icon: 'utensils',
    color: '#f59e0b',
    type: 'expense',
    is_default: true,
    display_order: 0,
    group_name: 'Courses & Vie quotidienne',
  },
  {
    name: 'Hygiène & maison',
    icon: 'home',
    color: '#3b82f6',
    type: 'expense',
    is_default: true,
    display_order: 1,
    group_name: 'Courses & Vie quotidienne',
  },
  {
    name: 'Animaux',
    icon: 'toy',
    color: '#ec4899',
    type: 'expense',
    is_default: true,
    display_order: 2,
    group_name: 'Courses & Vie quotidienne',
  },

  // Expense - Transport & Déplacements
  {
    name: 'Crédit voiture',
    icon: 'car',
    color: '#3b82f6',
    type: 'expense',
    is_default: true,
    display_order: 0,
    group_name: 'Transport & Déplacements',
  },
  {
    name: 'Carburant',
    icon: 'fuel',
    color: '#f59e0b',
    type: 'expense',
    is_default: true,
    display_order: 1,
    group_name: 'Transport & Déplacements',
  },
  {
    name: 'Péages',
    icon: 'toll',
    color: '#6b7280',
    type: 'expense',
    is_default: true,
    display_order: 2,
    group_name: 'Transport & Déplacements',
  },
  {
    name: 'Entretien véhicule',
    icon: 'wrench',
    color: '#ef4444',
    type: 'expense',
    is_default: true,
    display_order: 3,
    group_name: 'Transport & Déplacements',
  },
  {
    name: 'Parking',
    icon: 'parking',
    color: '#8b5cf6',
    type: 'expense',
    is_default: true,
    display_order: 4,
    group_name: 'Transport & Déplacements',
  },
  {
    name: 'Transports en commun',
    icon: 'bus',
    color: '#10b981',
    type: 'expense',
    is_default: true,
    display_order: 5,
    group_name: 'Transport & Déplacements',
  },

  // Expense - Santé
  {
    name: 'Consultation médecin',
    icon: 'stethoscope',
    color: '#10b981',
    type: 'expense',
    is_default: true,
    display_order: 0,
    group_name: 'Santé',
  },
  {
    name: 'Pharmacie',
    icon: 'pill',
    color: '#ef4444',
    type: 'expense',
    is_default: true,
    display_order: 1,
    group_name: 'Santé',
  },

  // Expense - Vie sociale & Plaisir
  {
    name: 'Restaurants & cafés',
    icon: 'coffee',
    color: '#f59e0b',
    type: 'expense',
    is_default: true,
    display_order: 0,
    group_name: 'Vie sociale & Plaisir',
  },
  {
    name: 'Sorties & loisirs',
    icon: 'film',
    color: '#8b5cf6',
    type: 'expense',
    is_default: true,
    display_order: 1,
    group_name: 'Vie sociale & Plaisir',
  },
  {
    name: 'Shopping & vêtements',
    icon: 'shirt',
    color: '#ec4899',
    type: 'expense',
    is_default: true,
    display_order: 2,
    group_name: 'Vie sociale & Plaisir',
  },
  {
    name: 'Voyages & vacances',
    icon: 'luggage',
    color: '#3b82f6',
    type: 'expense',
    is_default: true,
    display_order: 3,
    group_name: 'Vie sociale & Plaisir',
  },

  // Expense - Enfants
  {
    name: 'Garde & cantine',
    icon: 'baby',
    color: '#ec4899',
    type: 'expense',
    is_default: true,
    display_order: 0,
    group_name: 'Enfants',
  },
  {
    name: 'Frais scolaires & fournitures',
    icon: 'book',
    color: '#3b82f6',
    type: 'expense',
    is_default: true,
    display_order: 1,
    group_name: 'Enfants',
  },
  {
    name: 'Activités & loisirs enfants',
    icon: 'toy',
    color: '#f59e0b',
    type: 'expense',
    is_default: true,
    display_order: 2,
    group_name: 'Enfants',
  },

  // Expense - Famille & Solidarité
  {
    name: "Envoi d'argent à l'étranger",
    icon: 'globe',
    color: '#10b981',
    type: 'expense',
    is_default: true,
    display_order: 0,
    group_name: 'Famille & Solidarité',
  },
  {
    name: 'Aide familiale',
    icon: 'family',
    color: '#8b5cf6',
    type: 'expense',
    is_default: true,
    display_order: 1,
    group_name: 'Famille & Solidarité',
  },

  // Expense - Cadeaux & Dons
  {
    name: 'Cadeaux',
    icon: 'gift',
    color: '#ec4899',
    type: 'expense',
    is_default: true,
    display_order: 0,
    group_name: 'Cadeaux & Dons',
  },
  {
    name: 'Dons & charité',
    icon: 'charity',
    color: '#16c47f',
    type: 'expense',
    is_default: true,
    display_order: 1,
    group_name: 'Cadeaux & Dons',
  },

  // Expense - Autres
  {
    name: 'Imprévus',
    icon: 'receipt',
    color: '#f59e0b',
    type: 'expense',
    is_default: true,
    display_order: 0,
    group_name: 'Autres',
  },
  {
    name: 'Divers',
    icon: 'more-horizontal',
    color: '#6b7280',
    type: 'expense',
    is_default: true,
    display_order: 1,
    group_name: 'Autres',
  },
];

export function useCategories() {
  const { user } = useAuth();
  const [categories, setCategories] = useState<Category[]>([]);
  const [groups, setGroups] = useState<CategoryGroup[]>([]);
  const [activeCategoryIds, setActiveCategoryIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const initialLoadDone = useRef(false);

  // Load categories and groups
  const loadData = useCallback(async () => {
    if (!user) return;

    try {
      // Only show loading spinner on initial load, not on refreshes
      if (!initialLoadDone.current) {
        setLoading(true);
      }
      setError(null);

      // Load user-owned groups only (not shared defaults)
      const { data: groupsData, error: groupsError } = await supabase
        .from('category_groups')
        .select('*')
        .eq('user_id', user.id)
        .order('type')
        .order('display_order');

      if (groupsError) throw groupsError;

      // Load user-owned categories only
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('categories')
        .select('*')
        .eq('user_id', user.id)
        .order('display_order');

      if (categoriesError) throw categoriesError;

      // Load active category IDs (used in transactions or budgets)
      const [transactionsResult, budgetsResult] = await Promise.all([
        supabase
          .from('transactions')
          .select('category_id')
          .eq('user_id', user.id)
          .not('category_id', 'is', null),
        supabase.from('category_budgets').select('category_id').eq('user_id', user.id),
      ]);

      const activeIds = new Set<string>();

      // Add category IDs from transactions
      if (transactionsResult.data) {
        transactionsResult.data.forEach((t) => {
          if (t.category_id) activeIds.add(t.category_id);
        });
      }

      // Add category IDs from budgets
      if (budgetsResult.data) {
        budgetsResult.data.forEach((b) => {
          if (b.category_id) activeIds.add(b.category_id);
        });
      }

      setActiveCategoryIds(activeIds);

      // If no groups found, seed defaults
      if (!groupsData || groupsData.length === 0) {
        await seedDefaultData();
        return;
      }

      setGroups(groupsData);
      setCategories(categoriesData || []);
    } catch (err) {
      console.error('Error loading categories:', err);
      setError('Failed to load categories');
    } finally {
      setLoading(false);
      initialLoadDone.current = true;
    }
  }, [user]);

  // Seed default groups and categories
  const seedDefaultData = async () => {
    if (!user) return;

    try {
      // Check if user already has any groups (not checking is_default anymore since we create user-owned copies)
      const { data: existingGroups } = await supabase
        .from('category_groups')
        .select('id')
        .eq('user_id', user.id)
        .limit(1);

      if (existingGroups && existingGroups.length > 0) {
        await loadData();
        return;
      }

      // Insert groups as user-owned (not shared defaults)
      const { data: insertedGroups, error: groupsError } = await supabase
        .from('category_groups')
        .insert(
          DEFAULT_GROUPS.map((g) => ({
            ...g,
            user_id: user.id,
            is_default: false, // User-owned copy, not a shared default
          }))
        )
        .select();

      if (groupsError) throw groupsError;
      if (!insertedGroups) throw new Error('Failed to insert groups');

      // Create a map of group names to IDs
      const groupMap = new Map(insertedGroups.map((g) => [g.name, g.id]));

      // Insert categories with proper group_id as user-owned
      const categoriesToInsert = DEFAULT_CATEGORIES_WITH_GROUPS.map(({ group_name, ...cat }) => ({
        ...cat,
        group_id: groupMap.get(group_name) || null,
        user_id: user.id,
      }));

      const { error: categoriesError } = await supabase
        .from('categories')
        .insert(categoriesToInsert);

      if (categoriesError) throw categoriesError;

      await loadData();
    } catch (err) {
      console.error('Error seeding default data:', err);
      setError('Failed to initialize categories');
    }
  };

  // Group CRUD operations
  const createGroup = async (data: { name: string; type: 'income' | 'expense' }) => {
    if (!user) return { success: false, error: 'Not authenticated' };

    try {
      // Check for duplicate: same name and type (case-insensitive)
      const normalizedName = data.name.trim().toLowerCase();
      const duplicate = groups.find(
        (g) => g.name.toLowerCase() === normalizedName && g.type === data.type
      );

      if (duplicate) {
        return {
          success: false,
          error: 'duplicate',
          duplicateGroup: duplicate,
        };
      }

      // Get max display_order for the type
      const maxOrder = groups
        .filter((g) => g.type === data.type)
        .reduce((max, g) => Math.max(max, g.display_order), -1);

      const { data: newGroup, error } = await supabase
        .from('category_groups')
        .insert([
          {
            name: data.name.trim(),
            type: data.type,
            display_order: maxOrder + 1,
            user_id: user.id,
            is_default: false,
          },
        ])
        .select()
        .single();

      if (error) throw error;

      setGroups((prev) => [...prev, newGroup]);
      return { success: true, data: newGroup };
    } catch (err) {
      console.error('Error creating group:', err);
      return { success: false, error: err };
    }
  };

  const updateGroup = async (id: string, data: Partial<CategoryGroupInsert>) => {
    if (!user) return { success: false, error: 'Not authenticated' };

    try {
      // If name is being changed, check for duplicates
      if (data.name) {
        const currentGroup = groups.find((g) => g.id === id);
        if (currentGroup) {
          const normalizedName = data.name.trim().toLowerCase();
          const targetType = data.type || currentGroup.type;
          const duplicate = groups.find(
            (g) => g.id !== id && g.name.toLowerCase() === normalizedName && g.type === targetType
          );

          if (duplicate) {
            return {
              success: false,
              error: 'duplicate',
              duplicateGroup: duplicate,
            };
          }
        }
      }

      // Trim name if provided
      const updateData = data.name ? { ...data, name: data.name.trim() } : data;

      // Optimistic update: update local state immediately
      const timestamp = new Date().toISOString();
      setGroups((prev) =>
        prev.map((g) => (g.id === id ? { ...g, ...updateData, updated_at: timestamp } : g))
      );

      const { data: updated, error } = await supabase
        .from('category_groups')
        .update({ ...updateData, updated_at: timestamp })
        .eq('id', id)
        .eq('user_id', user.id)
        .select('id');

      if (error) throw error;

      // Verify update succeeded
      if (!updated || updated.length === 0) {
        throw new Error('Failed to update group - permission denied');
      }

      return { success: true };
    } catch (err) {
      console.error('Error updating group:', err);
      // Revert optimistic update on error
      await loadData();
      return { success: false, error: err };
    }
  };

  const deleteGroup = async (id: string) => {
    if (!user) return { success: false, error: 'Not authenticated' };

    try {
      const group = groups.find((g) => g.id === id);
      if (group?.is_default) {
        return { success: false, error: 'Cannot delete default group' };
      }

      // First, unassign all categories from this group (must use user_id filter)
      const { error: updateError } = await supabase
        .from('categories')
        .update({ group_id: null, updated_at: new Date().toISOString() })
        .eq('group_id', id)
        .eq('user_id', user.id);

      if (updateError) throw updateError;

      // Then delete the group
      const { data: deleted, error: deleteError } = await supabase
        .from('category_groups')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id)
        .select('id');

      if (deleteError) throw deleteError;

      // Verify delete succeeded
      if (!deleted || deleted.length === 0) {
        throw new Error('Failed to delete group - permission denied');
      }

      // Optimistic update: update local state
      setCategories((prev) => prev.map((c) => (c.group_id === id ? { ...c, group_id: null } : c)));
      setGroups((prev) => prev.filter((g) => g.id !== id));

      return { success: true };
    } catch (err) {
      console.error('Error deleting group:', err);
      // Reload data to revert any partial state changes
      await loadData();
      return { success: false, error: err };
    }
  };

  const reorderGroups = async (groupIds: string[], _type: 'income' | 'expense') => {
    if (!user) return { success: false, error: 'Not authenticated' };

    try {
      // Optimistic update: update local state immediately
      setGroups((prev) => {
        const updatedGroups = [...prev];
        groupIds.forEach((id, index) => {
          const groupIndex = updatedGroups.findIndex((g) => g.id === id);
          if (groupIndex !== -1) {
            updatedGroups[groupIndex] = {
              ...updatedGroups[groupIndex],
              display_order: index,
            };
          }
        });
        return updatedGroups;
      });

      // Update database in parallel - update display_order for user's groups
      // Use .select() to verify each update actually succeeded (RLS may silently block)
      const timestamp = new Date().toISOString();
      const updatePromises = groupIds.map((id, index) =>
        supabase
          .from('category_groups')
          .update({
            display_order: index,
            updated_at: timestamp,
          })
          .eq('id', id)
          .eq('user_id', user.id)
          .select('id')
      );

      const results = await Promise.all(updatePromises);

      // Check for errors first
      const failedUpdate = results.find((r) => r.error);
      if (failedUpdate?.error) {
        console.error('Failed to update group:', failedUpdate.error);
        throw failedUpdate.error;
      }

      // Check that all updates actually affected rows (RLS might silently block)
      const failedCount = results.filter((r) => !r.data || r.data.length === 0).length;
      if (failedCount > 0) {
        console.error(`${failedCount} group updates were silently blocked (likely RLS)`);
        throw new Error(`Failed to update ${failedCount} groups - permission denied`);
      }

      return { success: true };
    } catch (err) {
      console.error('Error reordering groups:', err);
      // Reload data to revert optimistic update on error
      await loadData();
      return { success: false, error: err };
    }
  };

  // Category CRUD operations
  const createCategory = async (data: {
    name: string;
    type: 'income' | 'expense';
    group_id?: string;
    icon?: string;
    color?: string;
  }) => {
    if (!user) return { success: false, error: 'Not authenticated' };

    try {
      // Check for duplicate: same name in the same group (case-insensitive)
      const normalizedName = data.name.trim().toLowerCase();
      const duplicate = categories.find(
        (c) =>
          c.name.toLowerCase() === normalizedName &&
          c.group_id === (data.group_id || null) &&
          c.type === data.type
      );

      if (duplicate) {
        return {
          success: false,
          error: 'duplicate',
          duplicateCategory: duplicate,
        };
      }

      // Get max display_order for the group
      const relatedCategories = categories.filter((c) => c.group_id === data.group_id);
      const maxOrder = relatedCategories.reduce((max, c) => Math.max(max, c.display_order), -1);

      const { data: newCategory, error } = await supabase
        .from('categories')
        .insert([
          {
            name: data.name.trim(),
            type: data.type,
            group_id: data.group_id || null,
            icon: data.icon || 'tag',
            color: data.color || '#6b7280',
            display_order: maxOrder + 1,
            user_id: user.id,
            is_default: false,
          },
        ])
        .select()
        .single();

      if (error) throw error;

      setCategories((prev) => [...prev, newCategory]);
      window.dispatchEvent(new CustomEvent('achievements-need-refresh'));
      return { success: true, data: newCategory };
    } catch (err) {
      console.error('Error creating category:', err);
      return { success: false, error: err };
    }
  };

  const updateCategory = async (id: string, data: Partial<CategoryInsert>) => {
    if (!user) return { success: false, error: 'Not authenticated' };

    try {
      // If name is being changed, check for duplicates
      if (data.name) {
        const currentCategory = categories.find((c) => c.id === id);
        if (currentCategory) {
          const normalizedName = data.name.trim().toLowerCase();
          const targetGroupId =
            data.group_id !== undefined ? data.group_id : currentCategory.group_id;
          const duplicate = categories.find(
            (c) =>
              c.id !== id &&
              c.name.toLowerCase() === normalizedName &&
              c.group_id === targetGroupId &&
              c.type === currentCategory.type
          );

          if (duplicate) {
            return {
              success: false,
              error: 'duplicate',
              duplicateCategory: duplicate,
            };
          }
        }
      }

      // Trim name if provided
      const updateData = data.name ? { ...data, name: data.name.trim() } : data;

      // Optimistic update: update local state immediately
      const timestamp = new Date().toISOString();
      setCategories((prev) =>
        prev.map((c) => (c.id === id ? { ...c, ...updateData, updated_at: timestamp } : c))
      );

      const { data: updated, error } = await supabase
        .from('categories')
        .update({ ...updateData, updated_at: timestamp })
        .eq('id', id)
        .eq('user_id', user.id)
        .select('id');

      if (error) throw error;

      // Verify update succeeded
      if (!updated || updated.length === 0) {
        throw new Error('Failed to update category - permission denied');
      }

      return { success: true };
    } catch (err) {
      console.error('Error updating category:', err);
      // Revert optimistic update on error
      await loadData();
      return { success: false, error: err };
    }
  };

  const deleteCategory = async (id: string) => {
    if (!user) return { success: false, error: 'Not authenticated' };

    try {
      const category = categories.find((c) => c.id === id);
      if (category?.is_default) {
        return { success: false, error: 'Cannot delete default category' };
      }

      const { data, error } = await supabase
        .from('categories')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id)
        .select('id');

      if (error) throw error;

      // Verify delete succeeded
      if (!data || data.length === 0) {
        throw new Error('Failed to delete category - permission denied');
      }

      setCategories((prev) => prev.filter((c) => c.id !== id));
      return { success: true };
    } catch (err) {
      console.error('Error deleting category:', err);
      return { success: false, error: err };
    }
  };

  const reorderCategories = async (categoryIds: string[], _groupId: string | null) => {
    if (!user) return { success: false, error: 'Not authenticated' };

    try {
      // Optimistic update: update local state immediately
      setCategories((prev) => {
        const updatedCategories = [...prev];
        categoryIds.forEach((id, index) => {
          const catIndex = updatedCategories.findIndex((c) => c.id === id);
          if (catIndex !== -1) {
            updatedCategories[catIndex] = {
              ...updatedCategories[catIndex],
              display_order: index,
            };
          }
        });
        return updatedCategories;
      });

      // Update database - update display_order for user's categories
      // Use .select() to verify each update actually succeeded (RLS may silently block)
      const timestamp = new Date().toISOString();
      const updatePromises = categoryIds.map((id, index) =>
        supabase
          .from('categories')
          .update({
            display_order: index,
            updated_at: timestamp,
          })
          .eq('id', id)
          .eq('user_id', user.id)
          .select('id')
      );

      const results = await Promise.all(updatePromises);

      // Check for errors first
      const failedUpdate = results.find((r) => r.error);
      if (failedUpdate?.error) {
        console.error('Failed to update category:', failedUpdate.error);
        throw failedUpdate.error;
      }

      // Check that all updates actually affected rows (RLS might silently block)
      const failedCount = results.filter((r) => !r.data || r.data.length === 0).length;
      if (failedCount > 0) {
        console.error(`${failedCount} category updates were silently blocked (likely RLS)`);
        throw new Error(`Failed to update ${failedCount} categories - permission denied`);
      }

      return { success: true };
    } catch (err) {
      console.error('Error reordering categories:', err);
      // Reload data to revert optimistic update on error
      await loadData();
      return { success: false, error: err };
    }
  };

  const resetCategoriesToDefault = async () => {
    if (!user) return { success: false, error: 'Not authenticated' };

    try {
      // Get all user categories grouped by group_id and type
      const { data: userCategories, error: fetchError } = await supabase
        .from('categories')
        .select('*')
        .eq('user_id', user.id);

      if (fetchError) throw fetchError;
      if (!userCategories) return { success: false, error: 'No categories found' };

      // Sort categories by name within each group to establish consistent default order
      const categoryGroups = new Map<string | null, typeof userCategories>();
      userCategories.forEach((cat) => {
        const groupId = cat.group_id;
        if (!categoryGroups.has(groupId)) {
          categoryGroups.set(groupId, []);
        }
        categoryGroups.get(groupId)?.push(cat);
      });

      // Update display_order based on alphabetical order within each group
      const updates: { id: string; display_order: number }[] = [];
      categoryGroups.forEach((cats) => {
        // Sort alphabetically by name
        cats.sort((a, b) => a.name.localeCompare(b.name));
        cats.forEach((cat, index) => {
          updates.push({ id: cat.id, display_order: index });
        });
      });

      // Optimistic update: update local state immediately
      setCategories((prev) => {
        const updatedCategories = [...prev];
        updates.forEach(({ id, display_order }) => {
          const catIndex = updatedCategories.findIndex((c) => c.id === id);
          if (catIndex !== -1) {
            updatedCategories[catIndex] = {
              ...updatedCategories[catIndex],
              display_order,
            };
          }
        });
        return updatedCategories;
      });

      // Apply updates in parallel for better performance
      // Use .select() to verify updates succeeded (RLS may silently block)
      const timestamp = new Date().toISOString();
      const updatePromises = updates.map(({ id, display_order }) =>
        supabase
          .from('categories')
          .update({ display_order, updated_at: timestamp })
          .eq('id', id)
          .eq('user_id', user.id)
          .select('id')
      );

      const results = await Promise.all(updatePromises);
      const failedUpdate = results.find((r) => r.error);
      if (failedUpdate?.error) throw failedUpdate.error;

      // Check that all updates actually affected rows
      const failedCount = results.filter((r) => !r.data || r.data.length === 0).length;
      if (failedCount > 0) {
        console.error(`${failedCount} category reset updates were silently blocked`);
        throw new Error(`Failed to reset ${failedCount} categories - permission denied`);
      }

      return { success: true };
    } catch (err) {
      console.error('Error resetting categories order:', err);
      // Reload data to revert optimistic update on error
      await loadData();
      return { success: false, error: err };
    }
  };

  const moveCategoryToGroup = async (categoryId: string, newGroupId: string | null) => {
    if (!user) return { success: false, error: 'Not authenticated' };

    try {
      // Optimistic update: update local state immediately
      const timestamp = new Date().toISOString();
      setCategories((prev) =>
        prev.map((c) =>
          c.id === categoryId ? { ...c, group_id: newGroupId, updated_at: timestamp } : c
        )
      );

      const { data, error } = await supabase
        .from('categories')
        .update({ group_id: newGroupId, updated_at: timestamp })
        .eq('id', categoryId)
        .eq('user_id', user.id)
        .select('id');

      if (error) throw error;

      // Verify update succeeded
      if (!data || data.length === 0) {
        throw new Error('Failed to move category - permission denied');
      }

      return { success: true };
    } catch (err) {
      console.error('Error moving category:', err);
      // Reload data to revert optimistic update on error
      await loadData();
      return { success: false, error: err };
    }
  };

  // Toggle hidden state for a category
  const toggleCategoryHidden = async (categoryId: string, isHidden: boolean) => {
    if (!user) return { success: false, error: 'Not authenticated' };

    try {
      // Optimistic update
      setCategories((prev) =>
        prev.map((c) => (c.id === categoryId ? { ...c, is_hidden: !isHidden } : c))
      );

      const { data, error } = await supabase
        .from('categories')
        .update({ is_hidden: !isHidden, updated_at: new Date().toISOString() })
        .eq('id', categoryId)
        .eq('user_id', user.id)
        .select('id');

      if (error) throw error;

      // Verify update succeeded
      if (!data || data.length === 0) {
        throw new Error('Failed to update category - permission denied');
      }

      return { success: true };
    } catch (err) {
      console.error('Error toggling category hidden state:', err);
      // Revert optimistic update
      await loadData();
      return { success: false, error: err };
    }
  };

  // Toggle hidden state for a category group
  const toggleGroupHidden = async (groupId: string, isHidden: boolean) => {
    if (!user) return { success: false, error: 'Not authenticated' };

    try {
      // Optimistic update for group
      setGroups((prev) => prev.map((g) => (g.id === groupId ? { ...g, is_hidden: !isHidden } : g)));

      const { data, error } = await supabase
        .from('category_groups')
        .update({ is_hidden: !isHidden, updated_at: new Date().toISOString() })
        .eq('id', groupId)
        .eq('user_id', user.id)
        .select('id');

      if (error) throw error;

      // Verify update succeeded
      if (!data || data.length === 0) {
        throw new Error('Failed to update group - permission denied');
      }

      return { success: true };
    } catch (err) {
      console.error('Error toggling group hidden state:', err);
      // Revert optimistic update
      await loadData();
      return { success: false, error: err };
    }
  };

  // Organized data for display
  const groupsWithCategories = useMemo((): GroupWithCategories[] => {
    return groups.map((group) => ({
      ...group,
      categories: categories
        .filter((c) => c.group_id === group.id)
        .sort((a, b) => a.display_order - b.display_order),
    }));
  }, [groups, categories]);

  const incomeGroups = useMemo(() => {
    return groupsWithCategories
      .filter((g) => g.type === 'income')
      .sort((a, b) => a.display_order - b.display_order);
  }, [groupsWithCategories]);

  const expenseGroups = useMemo(() => {
    return groupsWithCategories
      .filter((g) => g.type === 'expense')
      .sort((a, b) => a.display_order - b.display_order);
  }, [groupsWithCategories]);

  // Ungrouped categories
  const ungroupedCategories = useMemo(() => {
    return categories.filter((c) => !c.group_id);
  }, [categories]);

  // Filter categories by type (visible only)
  const getIncomeCategories = useCallback(() => {
    return categories
      .filter((c) => c.type === 'income' && !c.is_hidden)
      .sort((a, b) => a.display_order - b.display_order);
  }, [categories]);

  const getExpenseCategories = useCallback(() => {
    return categories
      .filter((c) => c.type === 'expense' && !c.is_hidden)
      .sort((a, b) => a.display_order - b.display_order);
  }, [categories]);

  // Get visible categories grouped by their category group (for select dropdowns)
  const getVisibleCategoriesGrouped = useCallback(
    (type: 'income' | 'expense') => {
      // Get visible groups for this type, sorted by display_order
      const visibleGroups = groups
        .filter((g) => g.type === type && !g.is_hidden)
        .sort((a, b) => a.display_order - b.display_order);

      // Build grouped structure
      return visibleGroups.map((group) => ({
        group,
        categories: categories
          .filter((c) => c.group_id === group.id && !c.is_hidden)
          .sort((a, b) => a.display_order - b.display_order),
      }));
    },
    [groups, categories]
  );

  const getCategoryById = useCallback(
    (id: string) => {
      return categories.find((c) => c.id === id);
    },
    [categories]
  );

  const getGroupById = useCallback(
    (id: string) => {
      return groups.find((g) => g.id === id);
    },
    [groups]
  );

  const getCategoryIcon = useCallback((iconName: string | null) => {
    if (!iconName) return CATEGORY_ICONS['default'];
    // If it's a known key, return the mapped emoji
    if (CATEGORY_ICONS[iconName]) return CATEGORY_ICONS[iconName];
    // If it contains non-ASCII characters, it's likely a direct emoji
    // eslint-disable-next-line no-control-regex
    if (/[^\u0000-\u007F]/.test(iconName)) return iconName;
    return CATEGORY_ICONS['default'];
  }, []);

  const getActiveCategoryCountByGroup = useCallback(
    (groupId: string) => {
      const groupCategories = categories.filter((c) => c.group_id === groupId);
      return groupCategories.filter((c) => activeCategoryIds.has(c.id)).length;
    },
    [categories, activeCategoryIds]
  );

  useEffect(() => {
    loadData();
  }, [loadData]);

  return {
    // Data
    categories,
    groups,
    groupsWithCategories,
    incomeGroups,
    expenseGroups,
    ungroupedCategories,
    loading,
    error,
    activeCategoryIds,

    // Group operations
    createGroup,
    updateGroup,
    deleteGroup,
    reorderGroups,
    toggleGroupHidden,

    // Category operations
    createCategory,
    updateCategory,
    deleteCategory,
    reorderCategories,
    resetCategoriesToDefault,
    moveCategoryToGroup,
    toggleCategoryHidden,

    // Helpers
    loadData,
    getIncomeCategories,
    getExpenseCategories,
    getVisibleCategoriesGrouped,
    getCategoryById,
    getGroupById,
    getCategoryIcon,
    seedDefaultData,
    getActiveCategoryCountByGroup,
  };
}
